import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { ToastContext, type ToastApi, type ToastRequest, type ToastTone } from './ToastContext';

interface Toast extends ToastRequest {
  readonly id: string;
  readonly tone: ToastTone;
}

/** What keeps a toast on screen: the pointer over it, or keyboard focus inside it. */
type Hold = 'pointer' | 'focus';

/** The time left on a toast that is on a timer. */
interface Countdown {
  readonly durationMs: number;
  timer: ReturnType<typeof setTimeout> | undefined;
  readonly holds: Set<Hold>;
}

const TONE_ICON: Record<ToastTone, IconName> = {
  neutral: 'info',
  success: 'check',
  warning: 'alert',
  danger: 'alert',
};

const DEFAULT_DURATION_MS = 6000;
const ACTION_DURATION_MS = 10000;

/** How long a toast stays when the caller did not say; 0 means until dismissed. */
function defaultDuration(tone: ToastTone, hasAction: boolean): number {
  if (tone === 'warning' || tone === 'danger') {
    return 0;
  }
  return hasAction ? ACTION_DURATION_MS : DEFAULT_DURATION_MS;
}

/**
 * Hosts the toast region.
 *
 * Two live regions, not one, and that is the reason this component exists at
 * all. A polite region waits for the screen reader to finish what it is saying,
 * which is right for "Job saved". An assertive region interrupts, which is
 * right for "Could not save — the job was changed by someone else" and wrong
 * for everything else. Putting both kinds of message into a single region
 * forces a choice between nagging and losing failures.
 *
 * Both regions are rendered unconditionally and empty, because a live region
 * that is inserted at the same moment as its content is frequently not
 * announced at all.
 *
 * A toast on a timer is never taken away from someone who is using it: while
 * the pointer is over it or focus is inside it, the timer stops, and when they
 * leave it starts again from the full time. Otherwise a keyboard user reaching
 * for "Undo" would find focus dropped on the page as the button vanished
 * (WCAG 2.2.1).
 *
 * @param children the application
 * @returns the provider and the toast region
 */
export function ToastProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const countdowns = useRef(new Map<string, Countdown>());
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    const countdown = countdowns.current.get(id);
    if (countdown !== undefined) {
      clearTimeout(countdown.timer);
      countdowns.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const startCountdown = useCallback(
    (id: string, countdown: Countdown) => {
      countdown.timer = setTimeout(() => {
        dismiss(id);
      }, countdown.durationMs);
    },
    [dismiss],
  );

  const hold = useCallback((id: string, reason: Hold) => {
    const countdown = countdowns.current.get(id);
    if (countdown !== undefined) {
      countdown.holds.add(reason);
      clearTimeout(countdown.timer);
      countdown.timer = undefined;
    }
  }, []);

  const release = useCallback(
    (id: string, reason: Hold) => {
      const countdown = countdowns.current.get(id);
      if (countdown === undefined) {
        return;
      }
      countdown.holds.delete(reason);
      if (countdown.holds.size === 0 && countdown.timer === undefined) {
        startCountdown(id, countdown);
      }
    },
    [startCountdown],
  );

  const show = useCallback(
    (request: ToastRequest) => {
      counter.current += 1;
      const id = `toast-${String(counter.current)}`;
      const tone = request.tone ?? 'neutral';
      const toast: Toast = { ...request, id, tone };

      setToasts((current) => [...current, toast]);

      const durationMs = request.durationMs ?? defaultDuration(tone, request.action !== undefined);
      if (durationMs > 0) {
        const countdown: Countdown = { durationMs, timer: undefined, holds: new Set() };
        countdowns.current.set(id, countdown);
        startCountdown(id, countdown);
      }
      return id;
    },
    [startCountdown],
  );

  // Timers outlive nothing: a provider that goes away takes its toasts' clocks with it.
  useEffect(() => {
    const running = countdowns.current;
    return () => {
      for (const countdown of running.values()) {
        clearTimeout(countdown.timer);
      }
      running.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  const polite = toasts.filter((toast) => toast.tone === 'neutral' || toast.tone === 'success');
  const assertive = toasts.filter((toast) => toast.tone === 'warning' || toast.tone === 'danger');

  return (
    <ToastContext value={api}>
      {children}
      <div className="fo-toasts" data-testid="toast-region">
        {/* `aria-live` rather than role="status" / role="alert".
            The behaviour is identical — those roles are defined as live regions
            with exactly these politeness settings — but a role would put two
            permanent landmarks into the accessibility tree of every page in the
            product, and make "find the alert on this page" ambiguous for
            assistive technology and for tests alike. The messages are the
            alerts; their container is not. */}
        <div
          className="fo-toasts__region"
          aria-live="polite"
          aria-atomic="false"
          data-testid="toasts-polite"
        >
          {polite.map((toast) => (
            <ToastCard
              key={toast.id}
              toast={toast}
              onDismiss={dismiss}
              onHold={hold}
              onRelease={release}
            />
          ))}
        </div>
        <div
          className="fo-toasts__region"
          aria-live="assertive"
          aria-atomic="false"
          data-testid="toasts-assertive"
        >
          {assertive.map((toast) => (
            <ToastCard
              key={toast.id}
              toast={toast}
              onDismiss={dismiss}
              onHold={hold}
              onRelease={release}
            />
          ))}
        </div>
      </div>
    </ToastContext>
  );
}

function ToastCard({
  toast,
  onDismiss,
  onHold,
  onRelease,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
  onHold: (id: string, reason: Hold) => void;
  onRelease: (id: string, reason: Hold) => void;
}): React.JSX.Element {
  const { action } = toast;
  return (
    <div
      className="fo-toast"
      data-tone={toast.tone}
      // A finger is not hovering: a touch only ever rests on the toast for as long as it is down.
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch') {
          onHold(toast.id, 'pointer');
        }
      }}
      onPointerLeave={() => {
        onRelease(toast.id, 'pointer');
      }}
      onFocus={() => {
        onHold(toast.id, 'focus');
      }}
      onBlur={(event) => {
        // Focus moving from one button of the toast to the next has not left it.
        const next = event.relatedTarget;
        if (!(next instanceof Node && event.currentTarget.contains(next))) {
          onRelease(toast.id, 'focus');
        }
      }}
    >
      <Icon name={TONE_ICON[toast.tone]} className="fo-toast__icon" />
      <p className="fo-toast__message">{toast.message}</p>
      {action === undefined ? null : (
        <span className="fo-toast__action">
          <Button
            size="sm"
            onClick={() => {
              action.onAction();
              onDismiss(toast.id);
            }}
          >
            {action.label}
          </Button>
        </span>
      )}
      <Button
        variant="ghost"
        size="sm"
        icon="close"
        iconOnly
        onClick={() => {
          onDismiss(toast.id);
        }}
      >
        Dismiss
      </Button>
    </div>
  );
}
